import type { FragmentOf } from "@opensea/graphql"
import { readFragment } from "@opensea/graphql"
import { usePersona } from "@opensea/navigation"
import type { textColorVariants, VariantProps } from "@opensea/ui-kit"
import { isAddressEqual } from "@opensea/utils"
import { useMemo } from "react"
import { TokenPrice } from "@/components/price/TokenPrice"
import { useProfileAddress } from "../profile/ProfileAddressProvider"
import { ActivityPriceFragment } from "./ActivityPrice.fragment"
import type { ActivityFeedTableRowVariant } from "./types"

type Props = {
  activity: FragmentOf<typeof ActivityPriceFragment>
  className?: string
  context?: {
    variant?: ActivityFeedTableRowVariant
  }
}

export function ActivityPrice({
  activity: activityKey,
  className,
  context,
}: Props) {
  const { persona } = usePersona()
  const { variant } = context ?? {}
  const address = useProfileAddress()
  const activity = readFragment(ActivityPriceFragment, activityKey)

  // From a product standpoint, just show empty price if it's 0 for mints
  const price = activity.price?.token.unit === 0 ? null : activity.price
  const showColoredPrice = persona === "advanced"
  const showGreenPrice =
    showColoredPrice &&
    (variant === "profile"
      ? activity.__typename === "Sale" &&
        isAddressEqual(address, activity.from.address)
      : activity.__typename === "Sale" && activity.saleType === "LISTING")
  const showRedPrice =
    showColoredPrice &&
    (variant === "profile"
      ? activity.__typename === "Sale" &&
        isAddressEqual(address, activity.to?.address)
      : activity.__typename === "Sale" && activity.saleType === "OFFER")

  const color: VariantProps<typeof textColorVariants>["color"] = useMemo(() => {
    if (showGreenPrice) {
      return "success-1"
    }
    if (showRedPrice) {
      return "error-1"
    }
    return undefined
  }, [showGreenPrice, showRedPrice])

  if (activity.__typename === "Transfer") {
    return null
  }

  return (
    <TokenPrice
      className={className}
      conversion="historical"
      price={price}
      symbolColor={color}
      valueColor={color}
    />
  )
}
